import { useDispatch, useSelector } from 'react-redux';

import getWholesaler from '@/API/Wholesaler/getWholesaler';
import config from '@/config.json';
import { useQuery } from 'hooks/useCustomQuery';

/**
 * Loads the currently selected wholesaler into
 * `navigationLists.currentWholesalerData` on app start, and again whenever the
 * selected wholesaler changes.
 *
 * The wholesaler navigation list is only fetched when the wholesaler picker is
 * focused, so before this ran nothing in the app knew the current wholesaler's
 * name on first paint (Bug 2807). Call this once from the app shell — everything
 * else reads `currentWholesalerData` straight from the store.
 *
 * @returns {import('@tanstack/react-query').UseQueryResult}
 */
const useCurrentWholesalerData = () => {
    const dispatch = useDispatch();
    const { currentWholesaler } = useSelector((state) => state.navigationLists);
    const { requiredScope } = useSelector((state) => state.login);

    return useQuery({
        // Keyed to match useGetWholesalerByID so both share one cache entry
        queryKey: ['wholesaler', currentWholesaler],
        queryFn: () => getWholesaler(currentWholesaler),
        enabled:
            Boolean(currentWholesaler) &&
            requiredScope >= config.scope.wholesaler,
        onSuccess: (data) => {
            dispatch({
                type: 'CHANGE_CURRENT_WHOLESALER_DATA',
                // The ID is what guards the persisted copy against a stale
                // wholesaler, so never store the data without one
                payload: { ...data, id: data?.id || currentWholesaler },
            });
        },
        onError: (error) => {
            console.error('Failed to load current wholesaler:', error);
        },
        refetchOnWindowFocus: false,
    });
};

export default useCurrentWholesalerData;
